import type { Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import {
    createBranchStepFromCases,
    createCaseFromToolStepPair,
    createLoopCaseFromToolStepPair,
    createLoopStepFromCase,
} from '@toolproof-core/lib/_pure/step-creation';
import { extractSingleBranchableOutputResourceTypeHandle } from '@toolproof-core/lib/_pure/workflow-extraction';
import type { IdentifierFactory } from '@toolproof-core/lib/_contract/identifiers';
import type {
    MutableCase,
    MutableToolStep,
    MutableToolStepKey,
    MutableWorkflow,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { Draft } from '@/workflow-builder/_contract/draft';

export function mergeWorkflowStateEntries(
    committedWorkflowState: MutableWorkflowState,
    draftWorkflowState: MutableWorkflowState,
): MutableWorkflowState {
    const mergedWorkflowState: MutableWorkflowState = { ...committedWorkflowState };

    for (const [stepKey, draftRoleEntries] of Object.entries(draftWorkflowState)) {
        const committedRoleEntries = committedWorkflowState[stepKey as keyof MutableWorkflowState];
        mergedWorkflowState[stepKey as keyof MutableWorkflowState] = {
            ...(committedRoleEntries ?? {}),
            ...draftRoleEntries,
        };
    }

    return mergedWorkflowState;
}

export function deriveCanonicalBranchOutputTypeHandle(
    caseSetDraft: Extract<Draft, { draftKind: 'branch' }>['caseSetDraft'],
    toolMap: Readonly<Map<ToolHandle, Tool>>,
): string | null {
    const completedCases = caseSetDraft.cases.filter((candidate) => candidate.when && candidate.what);
    const firstWhat = completedCases[0]?.what;
    if (!firstWhat) {
        return null;
    }

    const firstTool = toolMap.get(firstWhat.toolHandle);
    if (!firstTool) {
        return null;
    }

    return extractSingleBranchableOutputResourceTypeHandle(firstTool);
}

export function collectDraftToolSteps(draft: Draft): MutableToolStep[] {
    switch (draft.draftKind) {
        case 'tool':
            return draft.toolStep ? [draft.toolStep] : [];
        case 'while':
        case 'for':
            return [draft.caseDraft.what, draft.caseDraft.when].filter((candidate): candidate is MutableToolStep => candidate !== null);
        case 'branch': {
            const caseToolSteps = draft.caseSetDraft.cases.flatMap((candidate) => [candidate.when, candidate.what]);
            const defaultToolSteps = draft.caseSetDraft.defaultCase ? [draft.caseSetDraft.defaultCase.when, draft.caseSetDraft.defaultCase.what] : [];
            return [...caseToolSteps, ...defaultToolSteps].filter((candidate): candidate is MutableToolStep => candidate !== null);
        }
    }
}

export function collectMutableWorkflowToolSteps(workflow: MutableWorkflow): MutableToolStep[] {
    const collected: MutableToolStep[] = [];
    for (const threadSteps of workflow.stepsByThreadIndex) {
        for (const step of threadSteps) {
            switch (step.stepKind) {
                case CONSTANTS.Enums.StepKind.tool:
                    collected.push(step);
                    break;
                case CONSTANTS.Enums.StepKind.while:
                case CONSTANTS.Enums.StepKind.for:
                    // Loop projection order is what-when.
                    collected.push(step.case.what, step.case.when);
                    break;
                case CONSTANTS.Enums.StepKind.branch:
                    // Branch projection order stays when-what.
                    for (const candidate of step.cases) {
                        collected.push(candidate.when, candidate.what);
                    }
                    break;
            }
        }
    }
    return collected;
}

export function hasAllRequiredBindingsForToolStep(args: {
    draftWorkflowState: MutableWorkflow['workflowState'];
    toolStep: MutableToolStep;
    includeDynamicRoles?: boolean;
}): boolean {
    const roleEntries = args.draftWorkflowState[args.toolStep.stepKey] ?? {};
    const dynamicRoleSet = new Set(args.toolStep.whenInputRoleBindingSpec?.dynamicInputRoleNames ?? []);
    for (const roleName of args.toolStep.roleBindingSpec.inputBindings) {
        if (!args.includeDynamicRoles && dynamicRoleSet.has(roleName)) {
            continue;
        }
        if (!(roleName in roleEntries)) {
            return false;
        }
    }

    return true;
}

function appendTopLevelStep(workflow: MutableWorkflow, step: MutableWorkflow['stepsByThreadIndex'][number][number]): MutableWorkflow {
    const nextStepsByThreadIndex = [...workflow.stepsByThreadIndex];
    const authoringSteps = [...(nextStepsByThreadIndex[0] ?? []), step];
    nextStepsByThreadIndex[0] = authoringSteps;
    return {
        ...workflow,
        stepsByThreadIndex: nextStepsByThreadIndex,
    };
}

export function mergeDraftIntoMutableWorkflow(
    draft: Draft,
    mutableWorkflow: MutableWorkflow,
    generateIdentifier: IdentifierFactory,
): MutableWorkflow {
    switch (draft.draftKind) {
        case 'tool': {
            if (!draft.toolStep) {
                return {
                    ...mutableWorkflow,
                    workflowState: mergeWorkflowStateEntries(
                        mutableWorkflow.workflowState,
                        draft.workflowState,
                    ),
                };
            }
            return {
                ...appendTopLevelStep(mutableWorkflow, draft.toolStep),
                workflowState: mergeWorkflowStateEntries(
                    mutableWorkflow.workflowState,
                    draft.workflowState,
                ),
            };
        }
        case 'while':
        case 'for': {
            if (!draft.caseDraft.what || !draft.caseDraft.when || !draft.caseDraft.loopFeedbackBindingSpec) {
                return mutableWorkflow;
            }
            const loopStep = createLoopStepFromCase(
                createLoopCaseFromToolStepPair(
                    draft.caseDraft.what,
                    draft.caseDraft.when,
                    draft.caseDraft.loopFeedbackBindingSpec,
                ),
                draft.draftKind,
                generateIdentifier,
            );
            return {
                ...appendTopLevelStep(mutableWorkflow, loopStep),
                workflowState: mergeWorkflowStateEntries(
                    mutableWorkflow.workflowState,
                    draft.workflowState,
                ),
            };
        }
        case 'branch': {
            const completeCases: MutableCase[] = draft.caseSetDraft.cases
                .filter((candidate): candidate is typeof candidate & { when: MutableToolStep; what: MutableToolStep } => Boolean(candidate.when && candidate.what))
                .map((candidate) => createCaseFromToolStepPair(candidate.what, candidate.when));

            const defaultCase = draft.caseSetDraft.defaultCase;
            if (defaultCase?.when && defaultCase?.what) {
                completeCases.push(createCaseFromToolStepPair(defaultCase.what, defaultCase.when));
            }

            if (completeCases.length === 0) {
                return mutableWorkflow;
            }

            const branchStep = createBranchStepFromCases(completeCases, generateIdentifier);
            return {
                ...appendTopLevelStep(mutableWorkflow, branchStep),
                workflowState: mergeWorkflowStateEntries(
                    mutableWorkflow.workflowState,
                    draft.workflowState,
                ),
            };
        }
    }
}

export function buildToolStepByKey(args: {
    mutableWorkflow: MutableWorkflow;
    draft: Draft;
}): Map<MutableToolStepKey, MutableToolStep> {
    const toolStepByKey = new Map<MutableToolStepKey, MutableToolStep>();
    for (const toolStep of collectMutableWorkflowToolSteps(args.mutableWorkflow)) {
        toolStepByKey.set(toolStep.stepKey, toolStep);
    }
    for (const toolStep of collectDraftToolSteps(args.draft)) {
        toolStepByKey.set(toolStep.stepKey, toolStep);
    }
    return toolStepByKey;
}
